import { useHistory } from 'react-router-dom';
import { useEffect, useCallback, useState, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, CircularProgress } from '@mui/material';
import { get, saveNA, updateActivityLog, getNA } from 'api/externalAccess';
import styles from './external-access.module.scss';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { useTranslation } from 'react-i18next';
import ExternalAccessLayout from 'components/common/ExternalClientLayout/ExternalAccessLayout';
import { setLoggedIn, setAccessKey, defineType, previewParam } from 'utils/externalAccess';
import actions from 'store/actions';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { getFullPathOrHome } from 'utils/paths';

export default function ExternalAccess() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const accessKey = params.get(searchParams.accessKey);
  const preview = params.get(searchParams.preview);
  const [externalConfig, setExternalConfig] = useState({});
  const na = useSelector((state) => state.na);
  const [loading, setLoading] = useState(true);

  const goToAssessment = useCallback(
    (id) => {
      history.push(
        getFullPathOrHome(
          `${paths.externalAccess}/${paths.redirect}/${id}?${
            searchParams.accessKey
          }=${accessKey}${previewParam(preview)}`,
        ),
      );
    },
    [history, accessKey, preview],
  );

  const createClientNA = useCallback(
    async (company, name) => {
      try {
        const response = await saveNA(company, name, na, accessKey);
        dispatch(actions.na.setNA(response));
        if (preview !== 'true') {
          await updateActivityLog({
            accessKey: accessKey,
            na: response,
            type: defineType(externalConfig.brokerNaId, response.id),
            updateLastUpdate: true,
          });
        }
        goToAssessment(response.id);
      } catch (error) {
        console.log(error);
      }
    },
    [accessKey, goToAssessment, na, dispatch, externalConfig.brokerNaId, preview],
  );

  const getExternalAccessConfig = useCallback(async () => {
    try {
      const response = await get(accessKey);
      
      if (response && response.brokerNaId) {
        try {
          const brokerNA = await getNA(response.brokerNaId, accessKey);
          if (brokerNA) {
            dispatch(actions.na.setNA(brokerNA));
          }
        } catch (naError) {
          console.warn('Failed to fetch broker NA:', naError);
        }
      }
      
      setExternalConfig(response);
      dispatch(actions.externalAccess.setExternalAccessConfig(response));
      
      if (response.readModeEnabled && !response.interactiveModeEnabled) {
        goToAssessment(response.brokerNaId);
      } else if (!response.readModeEnabled && response.interactiveModeEnabled) {
        if (response.clientNaId) {
          goToAssessment(response.clientNaId);
        } else {
          const company = {
            name: response.companyName,
            id: response.companyId,
            edpid: response.companyEdpid,
          };
          await createClientNA(company, response.name);
        }
      } else if (!response.readModeEnabled && !response.interactiveModeEnabled) {
        history.push(getFullPathOrHome(paths.externalAccessDenied));
      }
      setLoading(false);
    } catch (error) {
      setLoggedIn(false);
      setAccessKey('');
      setLoading(false);
      
      const errorStatus = error?.response?.status;
      if (errorStatus === 404) {
        history.push(getFullPathOrHome(paths.externalAccessDenied));
      } else {
        history.push(
          getFullPathOrHome(
            `${paths.externalAccessLogin}?${searchParams.accessKey}=${accessKey}${previewParam(
              preview,
            )}`,
          ),
        );
      }
    }
  }, [history, accessKey, goToAssessment, createClientNA, dispatch, preview]);

  useEffect(() => {
    getExternalAccessConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNa = () => {
    dispatch(actions.warning.setShowWarning(false));
    goToAssessment(externalConfig.brokerNaId);
  };

  const openClientNa = () => {
    const company = {
      name: externalConfig.companyName,
      id: externalConfig.companyId,
      edpid: externalConfig.companyEdpid,
    };
    externalConfig.clientNaId
      ? goToAssessment(externalConfig.clientNaId)
      : createClientNA(company, externalConfig.name);
  };
  const content = (
    <div className={styles.other_buttons}>
      {loading ? (
        <CircularProgress />
      ) : (
        <Fragment>
          <Button
            data-testid="ExternalAccessSharedNaButton"
            className={styles.other_buttons__btn}
            size="small"
            variant="contained"
            onClick={openNa}>
            <h3>{t('externalAccess.sharedNaTitle')}</h3>
            <p>{t('externalAccess.sharedNaDescription')}</p>
            <ArrowForwardIosIcon />
          </Button>
          <Button
            data-testid="ExternalAccessInteractiveToolsButton"
            className={styles.other_buttons__btn}
            size="small"
            variant="contained"
            onClick={openClientNa}>
            <div className={styles.other_buttons__title_container}>
              <EditIcon /> <h3>{t('externalAccess.interactiveToolsTitle')}</h3>
            </div>
            <p>{t('externalAccess.interactiveToolsDescription')}</p>
            <ArrowForwardIosIcon />
          </Button>
        </Fragment>
      )}
    </div>
  );
  return (
    <ExternalAccessLayout
      children={content}
      description={t('externalAccess.description')}></ExternalAccessLayout>
  );
}
